import { Platform, Linking } from 'react-native';
import {
  getCurrentUser,
  getSubscriptionDoc,
  setSubscriptionDoc,
} from '../utils/subscriptionFirestore';
import {
  Subscription,
  SubscriptionTier,
  SubscriptionPlatform,
  PurchaseResult,
} from '../types/subscription';
import { IS_TESTING, TEST_SUBSCRIPTION_DATA } from '../config/testing';
import {
  REVENUECAT_CONFIG,
  SUBSCRIPTION_PRODUCTS,
  isRevenueCatConfigured,
  isRevenueCatWebConfigured,
} from '../config/revenuecat';
import { revenueCatService } from './revenueCatService';
import { revenueCatWebService } from './revenueCatWebService';
import {
  getStoreKitActiveProductIds,
  mergeProductIds,
  pickPrimaryProductId,
  tierFromProductIds,
} from './storeKitSubscriptionService';
import type { CustomerInfo as WebCustomerInfo } from '@revenuecat/purchases-js';

export { SUBSCRIPTION_PRODUCTS };

const MOCK_PRODUCTS = [
  {
    id: 'standard_monthly',
    tier: 'standard',
    platform: 'web',
    price: 4.99,
    currency: 'USD',
    billingPeriod: 'monthly',
    trialDays: 7,
    description: 'Standard Plan (Monthly)',
  },
  {
    id: 'standard_yearly',
    tier: 'standard',
    platform: 'web',
    price: 49.99,
    currency: 'USD',
    billingPeriod: 'yearly',
    trialDays: 7,
    description: 'Standard Plan (Yearly)',
  },
  {
    id: 'premium_monthly',
    tier: 'premium',
    platform: 'web',
    price: 9.99,
    currency: 'USD',
    billingPeriod: 'monthly',
    trialDays: 7,
    description: 'Premium Plan (Monthly)',
  },
  {
    id: 'premium_yearly',
    tier: 'premium',
    platform: 'web',
    price: 99.99,
    currency: 'USD',
    billingPeriod: 'yearly',
    trialDays: 7,
    description: 'Premium Plan (Yearly)',
  },
];

function toIsoDate(value: Date | string | null | undefined): string | undefined {
  if (value == null) return undefined;
  if (value instanceof Date) return value.toISOString();
  return value;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function subscriptionFromNativeCustomerInfo(
  userId: string,
  customerInfo: import('react-native-purchases').CustomerInfo,
  overrides?: { tier?: SubscriptionTier; productId?: string }
): Subscription {
  const tier = overrides?.tier ?? revenueCatService.getTierFromCustomerInfo(customerInfo);
  const productId =
    overrides?.productId ??
    revenueCatService.getActiveProductId(customerInfo) ??
    customerInfo.activeSubscriptions.find(
      (id) => id.includes('premium') || id.includes('standard')
    );
  const platform = subscriptionService.getCurrentPlatform();
  const entitlement =
    tier === 'premium'
      ? customerInfo.entitlements.active[REVENUECAT_CONFIG.entitlements.premium]
      : tier === 'standard'
        ? customerInfo.entitlements.active[REVENUECAT_CONFIG.entitlements.standard]
        : undefined;
  const expirationDate = entitlement?.expirationDate;
  const endDate = toIsoDate(expirationDate);

  return {
    userId,
    tier,
    status: tier === 'free' ? 'expired' : 'active',
    platform,
    startDate: new Date().toISOString(),
    ...(endDate ? { endDate } : {}),
    autoRenew: entitlement?.willRenew ?? false,
    lastBillingDate: new Date().toISOString(),
    ...(endDate ? { nextBillingDate: endDate } : {}),
    ...(productId ? { productId } : {}),
    receiptData: JSON.stringify({
      originalAppUserId: customerInfo.originalAppUserId,
      activeSubscriptions: customerInfo.activeSubscriptions,
    }),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function subscriptionFromWebCustomerInfo(
  userId: string,
  customerInfo: WebCustomerInfo
): Subscription {
  const tier = revenueCatWebService.getTierFromCustomerInfo(customerInfo);
  const productId = revenueCatWebService.getActiveProductId(customerInfo);
  const entitlement =
    customerInfo.entitlements.active[REVENUECAT_CONFIG.entitlements.premium] ??
    customerInfo.entitlements.active[REVENUECAT_CONFIG.entitlements.standard];
  const expirationDate = entitlement?.expirationDate;
  const isTrial = entitlement?.periodType === 'trial';

  return {
    userId,
    tier,
    status: tier === 'free' ? 'expired' : isTrial ? 'trial' : 'active',
    platform: 'web',
    startDate: new Date().toISOString(),
    endDate: toIsoDate(expirationDate),
    trialEndDate: isTrial ? toIsoDate(expirationDate) : undefined,
    autoRenew: entitlement?.willRenew ?? false,
    lastBillingDate: new Date().toISOString(),
    nextBillingDate: toIsoDate(expirationDate),
    productId,
    receiptData: JSON.stringify({
      originalAppUserId: customerInfo.originalAppUserId,
      activeSubscriptions: Array.from(customerInfo.activeSubscriptions),
    }),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export const subscriptionService = {
  async ensureRevenueCatReady(userId?: string): Promise<void> {
    if (Platform.OS === 'web') {
      if (!isRevenueCatWebConfigured()) return;
      await revenueCatWebService.initialize(userId);
      return;
    }
    if (!isRevenueCatConfigured()) return;
    await revenueCatService.initialize(userId);
  },

  async getAvailableProducts(): Promise<any[]> {
    try {
      if (Platform.OS === 'web') {
        if (isRevenueCatWebConfigured()) {
          await subscriptionService.ensureRevenueCatReady(getCurrentUser()?.uid);
          const products = await revenueCatWebService.getAvailableProducts();
          if (products.length > 0) {
            return products;
          }
        }
        return MOCK_PRODUCTS;
      }

      if (IS_TESTING && !isRevenueCatConfigured()) {
        return Object.values(TEST_SUBSCRIPTION_DATA).map((product) => ({
          ...product,
          platform: Platform.OS,
          trialDays: 7,
        }));
      }

      await subscriptionService.ensureRevenueCatReady(getCurrentUser()?.uid);
      const products = await revenueCatService.getAvailableProducts();
      if (products.length > 0) {
        return products;
      }

      return MOCK_PRODUCTS;
    } catch (error) {
      console.error('Error fetching products:', error);
      return MOCK_PRODUCTS;
    }
  },

  getProductById(productId: string): any | undefined {
    return MOCK_PRODUCTS.find((product) => product.id === productId);
  },

  formatPrice(price: number, currency: string = 'USD'): string {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).format(price);
    } catch {
      return `$${price.toFixed(2)}`;
    }
  },

  getPriceFromProduct(product: any): number {
    return product.price ?? 0;
  },

  getCurrentPlatform(): SubscriptionPlatform {
    if (Platform.OS === 'ios') return 'ios';
    if (Platform.OS === 'android') return 'android';
    return 'web';
  },

  async initializeSubscription(userId: string): Promise<Subscription> {
    const subscription: Subscription = {
      userId,
      tier: 'free',
      status: 'active',
      platform: this.getCurrentPlatform(),
      startDate: new Date().toISOString(),
      autoRenew: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await setSubscriptionDoc(userId, subscription);
    return subscription;
  },

  async getCurrentSubscription(): Promise<Subscription | null> {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    return getSubscriptionDoc(currentUser.uid);
  },

  async updateSubscription(subscription: Subscription): Promise<void> {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    await setSubscriptionDoc(currentUser.uid, {
      ...subscription,
      updatedAt: new Date().toISOString(),
    });
  },

  async resolveNativeSubscriptionFromStores(
    userId: string,
    customerInfo: import('react-native-purchases').CustomerInfo
  ): Promise<Subscription | null> {
    const storeKitIds = await getStoreKitActiveProductIds();
    const mergedIds = mergeProductIds(customerInfo.activeSubscriptions, storeKitIds);
    const storeKitTier = tierFromProductIds(storeKitIds);
    const mergedTier = tierFromProductIds(mergedIds);
    const rcTier = revenueCatService.getTierFromCustomerInfo(customerInfo);

    const tierRank = (t: SubscriptionTier) => (t === 'premium' ? 2 : t === 'standard' ? 1 : 0);
    let tier =
      [storeKitTier, mergedTier, rcTier].sort((a, b) => tierRank(b) - tierRank(a))[0] ?? 'free';

    const existing = await getSubscriptionDoc(userId);
    const storeKitHasPremium = storeKitIds.some((id) => id.includes('premium'));

    // Don't let stale RevenueCat data downgrade when App Store / StoreKit still shows premium.
    if (
      existing?.tier === 'premium' &&
      tier === 'standard' &&
      (storeKitHasPremium || storeKitIds.length === 0)
    ) {
      tier = 'premium';
      if (__DEV__) {
        console.warn('Subscription sync: blocked RC downgrade; keeping premium', {
          storeKitIds,
          rcActive: customerInfo.activeSubscriptions,
        });
      }
    }

    if (tier === 'free') return null;

    let productId =
      pickPrimaryProductId(storeKitIds.length > 0 ? storeKitIds : mergedIds) ??
      revenueCatService.getActiveProductId(customerInfo);

    if (tier === 'premium' && productId && !productId.includes('premium')) {
      productId =
        pickPrimaryProductId(storeKitIds) ??
        pickPrimaryProductId(mergedIds) ??
        existing?.productId ??
        productId;
    }

    if (__DEV__) {
      console.log('Subscription sync resolved', {
        storeKitIds,
        rcActive: customerInfo.activeSubscriptions,
        rcTier,
        mergedTier,
        storeKitTier,
        resolvedTier: tier,
        resolvedProductId: productId,
        firestoreTier: existing?.tier,
      });
    }

    const subscription = subscriptionFromNativeCustomerInfo(userId, customerInfo, {
      tier,
      productId,
    });
    if (existing?.createdAt) {
      subscription.createdAt = existing.createdAt;
    }
    await this.updateSubscription(subscription);
    return subscription;
  },

  async applyNativeCustomerInfo(
    userId: string,
    customerInfo: import('react-native-purchases').CustomerInfo
  ): Promise<Subscription | null> {
    return this.resolveNativeSubscriptionFromStores(userId, customerInfo);
  },

  /** Sync App Store / Play purchases made outside Purchases.purchase* (e.g. iOS SubscriptionStoreView). */
  async syncSubscriptionFromStore(userId: string): Promise<Subscription | null> {
    if (Platform.OS === 'web') {
      return this.syncFromRevenueCat(userId);
    }
    if (!isRevenueCatConfigured()) return null;

    await revenueCatService.initialize(userId);
    const customerInfo = await revenueCatService.refreshCustomerInfoFromStore();
    if (!customerInfo) return null;
    return this.resolveNativeSubscriptionFromStores(userId, customerInfo);
  },

  /**
   * Retry store sync — RevenueCat can lag after SubscriptionStoreView plan changes.
   * Returns when tier/product changes or retries are exhausted.
   */
  async syncSubscriptionFromStoreWithRetry(
    userId: string,
    options?: { attempts?: number; delayMs?: number }
  ): Promise<Subscription | null> {
    const attempts = options?.attempts ?? 6;
    const delayMs = options?.delayMs ?? 2000;
    const previous = await getSubscriptionDoc(userId);
    let lastSynced: Subscription | null = null;

    for (let i = 0; i < attempts; i++) {
      lastSynced = await this.syncSubscriptionFromStore(userId);
      if (lastSynced) {
        const tierChanged = !previous || lastSynced.tier !== previous.tier;
        const productChanged = !previous || lastSynced.productId !== previous.productId;
        if (tierChanged || productChanged || i === attempts - 1) {
          return lastSynced;
        }
      }
      if (i < attempts - 1) {
        await delay(delayMs);
      }
    }

    return lastSynced;
  },

  async syncFromRevenueCat(userId: string): Promise<Subscription | null> {
    if (Platform.OS === 'web') {
      if (!isRevenueCatWebConfigured()) return null;
      await revenueCatWebService.initialize(userId);
      const customerInfo = await revenueCatWebService.getCustomerInfo();
      if (!customerInfo || !revenueCatWebService.isEntitlementActive(customerInfo)) {
        return null;
      }
      const subscription = subscriptionFromWebCustomerInfo(userId, customerInfo);
      await this.updateSubscription(subscription);
      return subscription;
    }

    if (!isRevenueCatConfigured()) return null;

    await revenueCatService.initialize(userId);
    const customerInfo = await revenueCatService.refreshCustomerInfoFromStore();
    if (!customerInfo) return null;
    return this.resolveNativeSubscriptionFromStores(userId, customerInfo);
  },

  async purchaseSubscription(
    productId: string,
    options?: { receiptData?: unknown; revenueCatPackage?: import('@revenuecat/purchases-js').Package }
  ): Promise<PurchaseResult> {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) {
        throw new Error('No authenticated user');
      }

      const platform = this.getCurrentPlatform();

      if (platform === 'web') {
        return await this.purchaseWebSubscription(productId, options);
      }

      if (IS_TESTING && !isRevenueCatConfigured()) {
        return await this.simulateTestPurchase(productId);
      }

      await revenueCatService.initialize(currentUser.uid);
      const result = await revenueCatService.purchaseProduct(productId);

      if (result.userCancelled) {
        return { success: false, error: 'Purchase cancelled' };
      }

      if (!result.success || !result.customerInfo) {
        return { success: false, error: result.error ?? 'Purchase failed' };
      }

      const subscription = await this.resolveNativeSubscriptionFromStores(
        currentUser.uid,
        result.customerInfo
      );
      if (!subscription) {
        return { success: false, error: 'Purchase completed but subscription could not be activated' };
      }

      return {
        success: true,
        subscription,
        receipt: result.customerInfo,
      };
    } catch (error) {
      console.error('Error purchasing subscription:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Purchase failed',
      };
    }
  },

  async purchaseWebSubscription(
    productId: string,
    options?: { receiptData?: unknown; revenueCatPackage?: import('@revenuecat/purchases-js').Package }
  ): Promise<PurchaseResult> {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      throw new Error('No authenticated user');
    }

    if (isRevenueCatWebConfigured()) {
      await revenueCatWebService.initialize(currentUser.uid);
      const result = await revenueCatWebService.purchaseProduct(
        productId,
        currentUser.email ?? undefined,
        options?.revenueCatPackage
      );

      if (result.userCancelled) {
        return { success: false, error: 'Purchase cancelled' };
      }

      if (!result.success || !result.customerInfo) {
        return {
          success: false,
          error: result.error ?? 'Unable to open checkout. Please try again.',
        };
      }

      const subscription = subscriptionFromWebCustomerInfo(
        currentUser.uid,
        result.customerInfo
      );
      await this.updateSubscription(subscription);
      return { success: true, subscription, receipt: result.customerInfo };
    }

    return {
      success: false,
      error:
        'Web billing is not configured. Set EXPO_PUBLIC_REVENUECAT_WEB_API_KEY in .env.',
    };
  },

  async simulateTestPurchase(productId: string): Promise<PurchaseResult> {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) {
        throw new Error('No authenticated user');
      }

      const testProduct =
        TEST_SUBSCRIPTION_DATA[productId as keyof typeof TEST_SUBSCRIPTION_DATA];
      if (!testProduct) {
        throw new Error('Invalid test product ID');
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));

      const mockReceipt = {
        orderId: `test_order_${Date.now()}`,
        purchaseToken: `test_token_${Date.now()}`,
        productId,
        purchaseState: 1,
        isAcknowledged: true,
      };

      const subscription: Subscription = {
        userId: currentUser.uid,
        tier: this.getProductTier(productId),
        status: 'active',
        platform: Platform.OS === 'ios' ? 'ios' : 'android',
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        autoRenew: true,
        lastBillingDate: new Date().toISOString(),
        nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        productId,
        receiptData: JSON.stringify(mockReceipt),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      await this.updateSubscription(subscription);
      return { success: true, subscription, receipt: mockReceipt };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Test purchase failed',
      };
    }
  },

  async restorePurchases(): Promise<PurchaseResult> {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) {
        throw new Error('No authenticated user');
      }

      if (Platform.OS === 'web') {
        if (isRevenueCatWebConfigured()) {
          await revenueCatWebService.initialize(currentUser.uid);
          const result = await revenueCatWebService.restorePurchases();
          if (!result.success || !result.customerInfo) {
            return { success: false, error: result.error ?? 'No active subscription found' };
          }
          const subscription = subscriptionFromWebCustomerInfo(
            currentUser.uid,
            result.customerInfo
          );
          await this.updateSubscription(subscription);
          return { success: true, subscription };
        }

        const currentSubscription = await this.getCurrentSubscription();
        if (currentSubscription && this.isSubscriptionActive(currentSubscription)) {
          return { success: true, subscription: currentSubscription };
        }
        return { success: false, error: 'No active subscription found' };
      }

      if (!isRevenueCatConfigured()) {
        return { success: false, error: 'No active subscription found' };
      }

      await revenueCatService.initialize(currentUser.uid);
      const customerInfo = await revenueCatService.refreshCustomerInfoFromStore();
      if (!customerInfo) {
        return { success: false, error: 'No active subscription found' };
      }

      const subscription = await this.resolveNativeSubscriptionFromStores(
        currentUser.uid,
        customerInfo
      );
      if (!subscription) {
        return { success: false, error: 'No active subscription found' };
      }

      return { success: true, subscription };
    } catch (error) {
      console.error('Error restoring purchases:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Restore failed',
      };
    }
  },

  async getSubscriptionManagementUrl(): Promise<string | null> {
    if (Platform.OS === 'web') {
      if (!isRevenueCatWebConfigured()) return null;
      const customerInfo = await revenueCatWebService.getCustomerInfo();
      return revenueCatWebService.getManagementURL(customerInfo) ?? null;
    }
    if (!isRevenueCatConfigured()) return null;
    const customerInfo = await revenueCatService.getCustomerInfo();
    return revenueCatService.getManagementURL(customerInfo) ?? null;
  },

  async cancelSubscription(): Promise<void> {
    const url = await this.getSubscriptionManagementUrl();
    if (url) {
      await Linking.openURL(url);
      return;
    }

    const currentSubscription = await this.getCurrentSubscription();
    if (!currentSubscription) {
      throw new Error('No subscription found');
    }

    const updatedSubscription: Subscription = {
      ...currentSubscription,
      status: 'cancelled',
      autoRenew: false,
      updatedAt: new Date().toISOString(),
    };
    await this.updateSubscription(updatedSubscription);
  },

  isSubscriptionActive(subscription: Subscription | null): boolean {
    if (!subscription) return false;

    if (subscription.status === 'active' || subscription.status === 'trial') {
      if (subscription.endDate) {
        return new Date(subscription.endDate) > new Date();
      }
      return true;
    }
    return false;
  },

  async checkTrialExpiration(): Promise<void> {
    try {
      const currentSubscription = await this.getCurrentSubscription();
      if (!currentSubscription) return;

      if (
        currentSubscription.status === 'trial' &&
        currentSubscription.trialEndDate
      ) {
        const trialEndDate = new Date(currentSubscription.trialEndDate);
        if (new Date() > trialEndDate) {
          await this.updateSubscription({
            ...currentSubscription,
            status: 'expired',
            autoRenew: false,
            updatedAt: new Date().toISOString(),
          });
        }
      }
    } catch (error) {
      console.error('Error checking trial expiration:', error);
    }
  },

  async hasUserHadTrial(userId: string): Promise<boolean> {
    try {
      const subscription = await getSubscriptionDoc(userId);
      return !!subscription?.trialEndDate;
    } catch (error) {
      console.error('Error checking trial status:', error);
      return false;
    }
  },

  getProductTier(productId: string): SubscriptionTier {
    if (productId.includes('premium')) return 'premium';
    if (productId.includes('standard')) return 'standard';
    return 'free';
  },
};
